// quality.js — pick a render budget this device can hold at 60 fps, then
// keep adjusting it while the site runs (dynamic resolution). Nothing here
// changes what the visitor sees, only how many pixels are spent on it.

export function detectTier(renderer) {
  const gl = renderer.getContext();
  let gpu = '';
  try {
    // Firefox reports the real GPU in RENDERER and deprecates the extension
    const plain = String(gl.getParameter(gl.RENDERER) || '');
    const ext = /Firefox/.test(navigator.userAgent) ? null : gl.getExtension('WEBGL_debug_renderer_info');
    gpu = String((ext && gl.getParameter(ext.UNMASKED_RENDERER_WEBGL)) || plain);
  } catch (e) { /* some browsers hide it */ }
  const ua = navigator.userAgent;
  const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
  const cores = navigator.hardwareConcurrency || 4;
  const mem = navigator.deviceMemory || 4;
  const forced = new URLSearchParams(location.search).get('tier');
  let tier = 'high';
  if (/SwiftShader|llvmpipe|Software|Basic Render|Microsoft Basic/i.test(gpu)) tier = 'low';
  else if (mobile) {
    tier = cores >= 6 && mem >= 4 ? 'mid' : 'low';
    if (/Adreno \(TM\) [345]\d\d|Mali-(T|G[35])|PowerVR|Intel/i.test(gpu)) tier = 'low';
  } else if (/Intel/i.test(gpu) && !/Iris Xe|Arc/i.test(gpu)) tier = 'mid';
  if (forced && TIERS[forced]) tier = forced;
  return { tier, gpu, mobile };
}

// maxScale: device pixels per CSS pixel at best. The governor may go lower.
export const TIERS = {
  high: { maxScale: 2.0, minScale: 0.8, bloom: 5, msaa: 4, cars: 4200, span: 38, trailSteps: 64, moon: '2k' },
  mid: { maxScale: 1.6, minScale: 0.7, bloom: 4, msaa: 0, cars: 2600, span: 32, trailSteps: 32, moon: '1k' },
  low: { maxScale: 1.25, minScale: 0.6, bloom: 3, msaa: 0, cars: 1400, span: 26, trailSteps: 16, moon: '1k' },
};

// Dynamic resolution. Frame times are averaged over half a second; slow
// windows step the scale down, and after a long smooth stretch it tries a
// step back up. A step up that fails lowers the ceiling, so it settles
// instead of oscillating.
export class Governor {
  constructor(cfg, dpr, onChange) {
    this.ceil = Math.min(dpr, cfg.maxScale);
    this.min = Math.min(this.ceil, cfg.minScale);
    this.scale = this.ceil;
    this.onChange = onChange;
    this.acc = 0; this.n = 0; this.good = 0; this.warm = 2.5; this.fps = 60; this.lastUp = -1;
    this.capped = false; this.probe = null;
  }
  tick(dt) {
    if (this.warm > 0) { this.warm -= dt; return; } // shaders compiling, fonts, first uploads
    if (dt > 0.25) return; // tab switch or a hitch, not a trend
    this.acc += dt; this.n++;
    if (this.acc < 0.5) return;
    const ms = (this.acc / this.n) * 1000;
    this.fps = 1000 / ms;
    this.acc = 0; this.n = 0;
    // A step down that didn't help means the browser is capping the frame
    // rate (iOS Low Power Mode, 30 Hz screens): undo it and stop trying.
    if (this.probe) {
      const p = this.probe;
      this.probe = null;
      if (ms > p.ms * 0.92) { this.capped = true; this.set(p.scale); return; }
    }
    if (this.capped && ms < 40) return;
    if (ms > 21 && this.scale > this.min) {
      this.probe = { ms, scale: this.scale };
      if (this.lastUp >= 0 && performance.now() - this.lastUp < 4000) this.ceil = Math.max(this.min, this.scale * 0.94);
      this.set(Math.max(this.min, this.scale * (ms > 30 ? 0.8 : 0.88)));
      this.good = 0;
    } else if (ms < 17.8) {
      this.good += 0.5;
      if (this.good >= 5 && this.scale < this.ceil) { this.set(Math.min(this.ceil, this.scale * 1.08)); this.lastUp = performance.now(); this.good = 0; }
    } else this.good = 0;
  }
  set(s) {
    s = Math.round(s * 100) / 100;
    if (s === this.scale) return;
    this.scale = s;
    this.onChange(s);
  }
}
